import { ParsedMissionType } from "../store/types/MissionsTypes"
import { getPercentAmountOfTotal } from "../tools"
import { EventPlannedLine } from "./FlightlineEventUtils"

const maxErrorPercent = 60

interface FlightlineConflictErrorDict {
    // the flightplan id
    [flight_plan_id: number]: string[] // the flightline names that appear in multiple flightplans
}

export interface MissionFlightlineErrors {
    missionName: string
    conflictErrors: FlightlineConflictErrorDict // object of plan IDs and flightline names
    noMatchErrors: string[] // array of line names
    numberOfFlightLines: number
}

export const validateMajorFlightplanLineErrors = (
    parsedMissions: ParsedMissionType[],
    eventPlannedLines: EventPlannedLine[],
) => {
    // We currently don't validate for regex mismatching as users sometimes expect it and started clicking past other more relevant errors
    const onlyMajorErrors: MissionFlightlineErrors[] = []

    // Collection of every planned line name and every plan ID they appear with
    const plannedLineNamesDict: { [flightline_name: string]: number[] } = {}

    eventPlannedLines.forEach((plannedLine) => {
        // Planned Line has plan ID and flight line name
        if (plannedLine.flightPlanId && plannedLine.plannedLineName) {
            const lineName = plannedLine.plannedLineName
            // line name already in Dict from another planned line
            if (plannedLineNamesDict[lineName]) {
                plannedLineNamesDict[lineName] = [...plannedLineNamesDict[lineName], plannedLine.flightPlanId]
            } else {
                // set line name in dict
                plannedLineNamesDict[lineName] = [plannedLine.flightPlanId]
            }
        }
    })

    // Go through each parsed mission
    parsedMissions.forEach((mission) => {
        const numberOfFlightLines = mission.flightline_events.length
        const noMatchErrors: string[] = []

        const commonPlanConflicts: FlightlineConflictErrorDict = {}

        // Go through every line event
        mission.flightline_events.forEach((lineEvent) => {
            if (lineEvent.fe_name_from_original) {
                // Check if line event's line name is in the line name dictionary from planned lines
                if (plannedLineNamesDict[lineEvent.fe_name_from_original]) {
                    const conflictingPlanIds = plannedLineNamesDict[lineEvent.fe_name_from_original]
                    // Line name is in the dictionary with more than one instance of plan IDs
                    if (conflictingPlanIds.length && conflictingPlanIds.length > 1) {
                        // Get the corresponding plan IDs from the conflict dictionary
                        // the first plan ID the line will be set to, is the Dict key to find other line names with similar conflicts
                        const firstPlanId = conflictingPlanIds[0]
                        commonPlanConflicts[firstPlanId] = [
                            ...(commonPlanConflicts[firstPlanId] ?? []),
                            lineEvent.fe_name_from_original,
                        ]
                    }
                } else {
                    // has no match in planned lines
                    noMatchErrors.push(lineEvent.fe_name_from_original)
                }
            }
        })

        // Number of lines that have conflicting flightplan IDs
        const numberOfConflictErrors = Object.values(commonPlanConflicts).reduce((total: number, conflictArray) => {
            // ConflictArray contains all flightlineNames that have been assigned the same flightplan ID
            return total + conflictArray.length
        }, 0)
        const hasMajorConflictErrors = !!numberOfConflictErrors //&& getPercentAmountOfTotal(numberOfConflictErrors, numberOfFlightLines) > maxErrorPercent

        // Number of lines with no matches exceeds 60% check
        const numberOfNoMatchErrors = noMatchErrors.length
        const hasMajorNoMatchErrors =
            !!numberOfNoMatchErrors &&
            getPercentAmountOfTotal(numberOfNoMatchErrors, numberOfFlightLines) > maxErrorPercent

        if (hasMajorConflictErrors || hasMajorNoMatchErrors) {
            onlyMajorErrors.push({
                missionName: mission.mission_name,
                noMatchErrors: hasMajorNoMatchErrors ? noMatchErrors : [],
                conflictErrors: hasMajorConflictErrors ? commonPlanConflicts : {},
                numberOfFlightLines,
            })
        }
    }) // end of parsedMissions.forEach
    return onlyMajorErrors
}
